import { defineStore, storeToRefs } from "pinia"
import { ref, watch, computed, watchEffect, type Ref } from "vue"
import { useAuthStore } from "@/store/auth.store"
import { UserSettingsRepository } from "@/repositories/user-settings-repository"
import { UserSettings } from "@/models/user_settings/user-settings.model"
import { DarkTheme } from "@/enums/theme-choices"
import type { User } from "@/models/user.model"
import { useStorage, usePreferredDark } from "@vueuse/core"
import { LayoutColumn } from "@/models/user_settings/layout-column.model"

export const useUserSettingsStore = defineStore("userSettingsStore", () => {
  const authStore = useAuthStore()
  const { activeUser } = storeToRefs(authStore)

  const userSettings: Ref<UserSettings | undefined> = ref()
  const storedTheme = useStorage<DarkTheme>("AppSettingsTheme", DarkTheme.LIGHT)

  async function saveUserSettings() {
    try {
      const payload = {
        ...(userSettings.value as UserSettings),
        layoutColumnConfigs: (userSettings.value?.layoutColumnConfigs || []).filter(
          (column) => !LayoutColumn.getNonPersistedComponents().includes(column.component)
        )
      }
      await new UserSettingsRepository().user_update(payload)
      return Promise.resolve(true)
    } catch (e) {
      console.log(e)
      return Promise.resolve(false)
    }
  }

  function rgbToHex(rgb: string): string {
    const rgbArray = rgb.match(/\d+/g)

    if (!rgbArray) {
      throw new Error("Invalid RGB format.")
    }

    const hexValue = rgbArray.map((component) => {
      const hex = parseInt(component).toString(16)
      return hex.length === 1 ? "0" + hex : hex
    })

    return "#" + hexValue.join("")
  }

  function applyMetaTheme() {
    const metaThemeColor = document.querySelector("meta[name='theme-color']")
    if (metaThemeColor) {
      const rootStyles = getComputedStyle(document.documentElement)
      const primaryColor = rootStyles.getPropertyValue("--color-primary-lighten-1")

      try {
        const hexValue = rgbToHex(primaryColor)
        metaThemeColor.setAttribute("content", hexValue)
      } catch (error) {
        console.log(error)
      }
    }
  }

  const preferredDark = usePreferredDark()

  const isDarkMode = computed(() => {
    if (storedTheme.value === DarkTheme.SYSTEM) {
      return preferredDark.value
    }
    return storedTheme.value === DarkTheme.DARK
  })

  watchEffect(() => {
    if (isDarkMode.value) {
      document.documentElement.classList.add(DarkTheme.DARK)
      document.documentElement.classList.remove(DarkTheme.LIGHT)
    } else {
      document.documentElement.classList.add(DarkTheme.LIGHT)
      document.documentElement.classList.remove(DarkTheme.DARK)
    }
  })

  function setDarkMode(theme: DarkTheme) {
    storedTheme.value = theme
  }

  watch(
    activeUser,
    (val: User | null) => {
      if (val) {
        userSettings.value = new UserSettings(val.userSettings)
        applyMetaTheme()
      }
    },
    { immediate: true }
  )

  return {
    userSettings,
    saveUserSettings,
    setDarkMode,
    isDarkMode,
    storedTheme
  }
})
